import { useState } from "react";
import {
  CogIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { trpc } from "../../lib/api/trpc";
import { useSupportedFormats } from "../../features/documents/hooks/useSupportedFormats";

type Strategy = {
  id: string;
  name: string;
  description: string | null;
  parserType: string;
  extractorType: string;
  chunkingType: string;
  documentTypes: string[];
  isActive: boolean;
  isDefault: boolean;
  version: number;
  extractorConfig: any;
};

export default function StrategiesPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Document-type options come from the backend parser registry — the single
  // source of truth for supported formats.
  const { formatOptions: mimeOptions, isLoading: formatsLoading } =
    useSupportedFormats();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  // Create form state
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newDocTypes, setNewDocTypes] = useState<string[]>(["application/pdf"]);
  const [newParserType, setNewParserType] = useState("pdf");
  const [newExtractorType, setNewExtractorType] = useState(
    "anthropic-technical",
  );
  const [newChunkingType, setNewChunkingType] = useState("semantic");
  const [newFocusAreas, setNewFocusAreas] = useState<string[]>([]);
  const [newFocusAreaInput, setNewFocusAreaInput] = useState("");
  const [newCustomInstructions, setNewCustomInstructions] = useState("");

  // @ts-ignore - tRPC type issues
  const {
    data: strategies,
    isLoading,
    refetch,
  } = trpc.strategies.list.useQuery({});

  // @ts-ignore - tRPC type issues
  const createMutation = trpc.strategies.create.useMutation({
    onSuccess: () => {
      refetch();
      setShowCreateForm(false);
      resetCreateForm();
      setMessage("Strategy created successfully");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(`Error: ${err.message}`);
    },
  });

  // @ts-ignore - tRPC type issues
  const toggleMutation = trpc.strategies.toggleActive.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  // @ts-ignore - tRPC type issues
  const updateMutation = trpc.strategies.update.useMutation({
    onSuccess: () => {
      refetch();
      setEditingId(null);
      setMessage("Strategy updated successfully");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(`Error: ${err.message}`);
    },
  });

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDocTypes, setEditDocTypes] = useState<string[]>([]);
  const [editParserType, setEditParserType] = useState("");
  const [editExtractorType, setEditExtractorType] = useState("");
  const [editChunkingType, setEditChunkingType] = useState("");
  const [editFocusAreas, setEditFocusAreas] = useState<string[]>([]);
  const [editFocusAreaInput, setEditFocusAreaInput] = useState("");
  const [editCustomInstructions, setEditCustomInstructions] = useState("");

  const startEditing = (strategy: Strategy) => {
    setEditingId(strategy.id);
    setEditName(strategy.name);
    setEditDescription(strategy.description || "");
    setEditDocTypes(strategy.documentTypes);
    setEditParserType(strategy.parserType);
    setEditExtractorType(strategy.extractorType);
    setEditChunkingType(strategy.chunkingType);
    setEditFocusAreas(strategy.extractorConfig?.focusAreas || []);
    setEditCustomInstructions(
      strategy.extractorConfig?.customInstructions || "",
    );
    setEditFocusAreaInput("");
  };

  const handleUpdate = () => {
    if (!editingId || !editName.trim()) return;
    updateMutation.mutate({
      id: editingId,
      name: editName.trim(),
      description: editDescription.trim() || undefined,
      documentTypes: editDocTypes,
      config: {
        parserType: editParserType as "pdf" | "docx" | "xlsx" | "text",
        extractorType: editExtractorType,
        chunkingType: editChunkingType,
        extractorConfig: {
          ...(editFocusAreas.length > 0 && { focusAreas: editFocusAreas }),
          ...(editCustomInstructions && {
            customInstructions: editCustomInstructions,
          }),
        },
      },
    });
  };

  const handleAddEditFocusArea = () => {
    const trimmed = editFocusAreaInput
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    if (trimmed && !editFocusAreas.includes(trimmed)) {
      setEditFocusAreas([...editFocusAreas, trimmed]);
      setEditFocusAreaInput("");
    }
  };

  const resetCreateForm = () => {
    setNewName("");
    setNewDescription("");
    setNewDocTypes(["application/pdf"]);
    setNewParserType("pdf");
    setNewExtractorType("anthropic-technical");
    setNewChunkingType("semantic");
    setNewFocusAreas([]);
    setNewFocusAreaInput("");
    setNewCustomInstructions("");
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMutation.mutate({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      documentTypes: newDocTypes,
      config: {
        parserType: newParserType as "pdf" | "docx" | "xlsx" | "text",
        extractorType: newExtractorType,
        chunkingType: newChunkingType,
        extractorConfig: {
          ...(newFocusAreas.length > 0 && { focusAreas: newFocusAreas }),
          ...(newCustomInstructions && {
            customInstructions: newCustomInstructions,
          }),
        },
      },
    });
  };

  const handleAddFocusArea = () => {
    const trimmed = newFocusAreaInput.trim().toLowerCase().replace(/\s+/g, "_");
    if (trimmed && !newFocusAreas.includes(trimmed)) {
      setNewFocusAreas([...newFocusAreas, trimmed]);
      setNewFocusAreaInput("");
    }
  };

  const handleToggleDocType = (mime: string) => {
    setNewDocTypes((prev) =>
      prev.includes(mime) ? prev.filter((m) => m !== mime) : [...prev, mime],
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  const strategyList = (strategies || []) as Strategy[];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
          Extraction Strategies
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage system-wide extraction strategies that can be assigned to
          organizations
        </p>
      </div>

      {/* Strategy List */}
      <div className="space-y-4 mb-6">
        {strategyList.length === 0 ? (
          <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No strategies found. Create one below.
            </p>
          </div>
        ) : (
          strategyList.map((strategy) => (
            <div
              key={strategy.id}
              className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6"
            >
              {editingId === strategy.id ? (
                /* Edit Form */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Parser
                      </label>
                      <select
                        value={editParserType}
                        onChange={(e) => setEditParserType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                      >
                        <option value="pdf">PDF</option>
                        <option value="docx">DOCX</option>
                        <option value="xlsx">XLSX</option>
                        <option value="text">Text (Markdown / plain)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Extractor
                      </label>
                      <input
                        type="text"
                        value={editExtractorType}
                        onChange={(e) => setEditExtractorType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Chunking
                      </label>
                      <select
                        value={editChunkingType}
                        onChange={(e) => setEditChunkingType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                      >
                        <option value="semantic">Semantic (AI)</option>
                        <option value="fixed">Fixed Size</option>
                        <option value="smart">Smart (Regex)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Document Types
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {mimeOptions.length === 0 && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatsLoading
                            ? "Loading supported formats…"
                            : "Supported formats unavailable — check the API health endpoint"}
                        </span>
                      )}
                      {mimeOptions.map((opt) => (
                        <label
                          key={opt.mime}
                          className="inline-flex items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={editDocTypes.includes(opt.mime)}
                            onChange={() =>
                              setEditDocTypes((prev) =>
                                prev.includes(opt.mime)
                                  ? prev.filter((m) => m !== opt.mime)
                                  : [...prev, opt.mime],
                              )
                            }
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Focus Areas
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {editFocusAreas.map((area) => (
                        <span
                          key={area}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-tint text-accent text-sm"
                        >
                          {area}
                          <button
                            onClick={() =>
                              setEditFocusAreas(
                                editFocusAreas.filter((a) => a !== area),
                              )
                            }
                          >
                            <XMarkIcon className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editFocusAreaInput}
                        onChange={(e) => setEditFocusAreaInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleAddEditFocusArea()
                        }
                        placeholder="Add focus area"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={handleAddEditFocusArea}
                        className="px-3 py-2 bg-accent text-white rounded-lg text-sm hover:bg-accent-strong"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Custom Instructions
                    </label>
                    <textarea
                      value={editCustomInstructions}
                      onChange={(e) =>
                        setEditCustomInstructions(e.target.value)
                      }
                      maxLength={1000}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-darkmode-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      disabled={!editName.trim() || updateMutation.isLoading}
                      className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 transition-colors"
                    >
                      {updateMutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                /* Display Mode */
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <CogIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {strategy.name}
                      </h3>
                      {strategy.isDefault && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          Default
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          strategy.isActive
                            ? "bg-accent-tint text-accent"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                        }`}
                      >
                        {strategy.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    {strategy.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                        {strategy.description}
                      </p>
                    )}
                    <div className="mt-2 ml-8 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>Parser: {strategy.parserType}</span>
                      <span>Extractor: {strategy.extractorType}</span>
                      <span>Chunking: {strategy.chunkingType}</span>
                      <span>
                        Types:{" "}
                        {strategy.documentTypes
                          .map((t: string) => {
                            const opt = mimeOptions.find((o) => o.mime === t);
                            return opt?.label || t;
                          })
                          .join(", ")}
                      </span>
                      <span>v{strategy.version}</span>
                    </div>
                    {strategy.extractorConfig?.focusAreas?.length > 0 && (
                      <div className="mt-2 ml-8 flex flex-wrap gap-1">
                        {strategy.extractorConfig.focusAreas.map(
                          (area: string) => (
                            <span
                              key={area}
                              className="px-2 py-0.5 rounded-full bg-accent-tint text-accent text-xs"
                            >
                              {area}
                            </span>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEditing(strategy)}
                      className="text-xs px-3 py-1 rounded-lg border border-accent text-accent hover:bg-accent-tint transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        toggleMutation.mutate({
                          id: strategy.id,
                          isActive: !strategy.isActive,
                        })
                      }
                      className={`text-xs px-3 py-1 rounded-lg border transition-colors ${
                        strategy.isActive
                          ? "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700"
                          : "border-accent text-accent hover:bg-accent-tint"
                      }`}
                    >
                      {strategy.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create New Strategy */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-3">
            <PlusIcon className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Create New Strategy
            </h2>
          </div>
          {showCreateForm ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {showCreateForm && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Bioscience PDF Extractor"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="e.g. Optimised for protocol manuals and SOPs"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Types
              </label>
              <div className="flex flex-wrap gap-3">
                {mimeOptions.length === 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatsLoading
                      ? "Loading supported formats…"
                      : "Supported formats unavailable — check the API health endpoint"}
                  </span>
                )}
                {mimeOptions.map((opt) => (
                  <label
                    key={opt.mime}
                    className="inline-flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={newDocTypes.includes(opt.mime)}
                      onChange={() => handleToggleDocType(opt.mime)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Parser Type
                </label>
                <select
                  value={newParserType}
                  onChange={(e) => setNewParserType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                >
                  <option value="pdf">PDF</option>
                  <option value="docx">DOCX</option>
                  <option value="xlsx">XLSX</option>
                  <option value="text">Text (Markdown / plain)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Extractor Type
                </label>
                <input
                  type="text"
                  value={newExtractorType}
                  onChange={(e) => setNewExtractorType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Chunking Type
                </label>
                <select
                  value={newChunkingType}
                  onChange={(e) => setNewChunkingType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                >
                  <option value="semantic">Semantic (AI)</option>
                  <option value="fixed">Fixed Size</option>
                  <option value="smart">Smart (Regex)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Focus Areas
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {newFocusAreas.map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-tint text-accent text-sm"
                  >
                    {area}
                    <button
                      onClick={() =>
                        setNewFocusAreas(
                          newFocusAreas.filter((a) => a !== area),
                        )
                      }
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFocusAreaInput}
                  onChange={(e) => setNewFocusAreaInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddFocusArea()}
                  placeholder="Add focus area"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleAddFocusArea}
                  className="px-3 py-2 bg-accent text-white rounded-lg text-sm hover:bg-accent-strong"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Instructions
              </label>
              <textarea
                value={newCustomInstructions}
                onChange={(e) => setNewCustomInstructions(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Optional instructions appended to the AI prompt"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  resetCreateForm();
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-darkmode-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || createMutation.isLoading}
                className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 transition-colors"
              >
                {createMutation.isLoading ? "Creating..." : "Create Strategy"}
              </button>
            </div>
          </div>
        )}
      </div>

      {message && (
        <p
          className={`text-sm mt-4 ${message.startsWith("Error") ? "text-red-600" : "text-green-600"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}
